import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../../api";
import { Icon, useToast, Spinner } from "../../components/ui";

const PROCESSES = [["SUBMISSION", "Submission"], ["END_PROCESS", "End Process"], ["MODIFICATION", "Modification"], ["MORE_INFO", "More Info"]];
const PLABEL = Object.fromEntries(PROCESSES);

export function OutstandingReport() {
  const toast = useToast();
  const [allUsers, setAllUsers] = useState(null);
  const [group, setGroup] = useState("");
  const [userId, setUserId] = useState("");
  const [pct, setPct] = useState({});          // { PROCESS: "20" }
  const [preview, setPreview] = useState(null); // { PROCESS: {sampled, total, records:[...]} }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Audit sampling is available to ADMIN as well as SUPER_ADMIN, so it must NOT call the
    // super-admin-only /api/users (that 403'd for admins and logged them out). This endpoint
    // lives under /api/patient-records and already returns only ACTIVE production users.
    api.auditUsers()
      .then((us) => setAllUsers((us || []).filter((u) => (u.roles || []).includes("USER") && u.status === "ACTIVE")))
      .catch(() => setAllUsers([]));
  }, []);

  // groups that actually have production users
  const groups = useMemo(() => {
    const s = new Set();
    (allUsers || []).forEach((u) => (u.groups || []).forEach((g) => g && s.add(g)));
    return [...s].sort();
  }, [allUsers]);

  const usersInGroup = useMemo(
    () => (allUsers || []).filter((u) => !group || (u.groups || []).includes(group)),
    [allUsers, group]
  );
  const selectedUser = useMemo(() => (allUsers || []).find((u) => String(u.id) === String(userId)), [allUsers, userId]);

  // always show all four processes; the ones this user isn't assigned to are locked
  const userProcesses = PROCESSES;
  const userSheets = useMemo(() => new Set(selectedUser?.sheets || []), [selectedUser]);
  const isLocked = (k) => !userSheets.has(k);

  // when the user changes, default their assigned processes to 20% and leave the locked ones blank
  useEffect(() => {
    setPreview(null); setErr("");
    const init = {};
    PROCESSES.forEach(([k]) => { init[k] = userSheets.has(k) ? "20" : ""; });
    setPct(init);
  }, [userId, selectedUser]); // eslint-disable-line

  const onPctChange = (k, v) => {
    if (isLocked(k)) return;
    if (v === "" || /^\d{1,3}$/.test(v)) setPct((p) => ({ ...p, [k]: v }));
  };

  const activeProcs = () => userProcesses.filter(([k]) => !isLocked(k) && Number(pct[k]) > 0);

  const runView = async () => {
    setErr(""); setPreview(null);
    if (!userId) { setErr("Select a user."); return; }
    const procs = activeProcs();
    if (procs.length === 0) { setErr("Enter a percentage (1–100) for at least one process."); return; }
    for (const [k] of procs) { const n = Number(pct[k]); if (!(n >= 1 && n <= 100)) { setErr(`${PLABEL[k]}: enter 1–100.`); return; } }
    setBusy(true);
    try {
      const out = {};
      for (const [k] of procs) out[k] = await api.auditPreview(userId, k, Number(pct[k]));
      setPreview(out);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const sendToAudit = async () => {
    const procs = activeProcs();
    if (procs.length === 0) return;
    setBusy(true);
    try {
      let total = 0;
      for (const [k] of procs) { const r = await api.auditSend({ userId, process: k, percent: Number(pct[k]) }); total += (r?.sampled ?? r?.records?.length ?? 0); }
      toast(`Sent ${total} record(s) to the auditor`, "ok");
      setPreview(null);
    } catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  };

  if (allUsers === null) return <div className="empty"><Spinner /></div>;

  const box = { background: "#fff", border: "1px solid #e5e9f0", borderRadius: 12, padding: 18 };
  const lbl = { fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 5, display: "block" };

  return (
    <div className="jr-page" style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>Audit sampling</h2>
        <div className="cell-sub">Pick a user, choose a percentage per process, and a random sample of their completed work is pulled for quality audit.</div>
      </div>

      <div style={{ ...box, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: userProcesses.length ? 18 : 0 }}>
          <div style={{ minWidth: 200 }}>
            <label style={lbl}>Group</label>
            <select className="inp" style={{ width: "100%", height: 40 }} value={group} onChange={(e) => { setGroup(e.target.value); setUserId(""); }}>
              <option value="">All groups</option>
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 240 }}>
            <label style={lbl}>User</label>
            <select className="inp" style={{ width: "100%", height: 40 }} value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Select a user…</option>
              {usersInGroup.map((u) => <option key={u.id} value={u.id}>{u.casName ? `${u.fullName} (${u.casName})` : u.fullName}</option>)}
            </select>
          </div>
        </div>

        {selectedUser && (
          <>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#1f2733", margin: "4px 0 10px" }}>Sample % per process</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
              {userProcesses.map(([k, label]) => {
                const locked = isLocked(k);
                return (
                  <div key={k} style={{ border: "1px solid #e5e9f0", borderRadius: 10, padding: "10px 12px", background: locked ? "#f1f5f9" : "#f8fafc", opacity: locked ? 0.7 : 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: locked ? "#94a3b8" : "#334155", marginBottom: 6 }}>{label}</div>
                    {locked ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: "#94a3b8", height: 36 }}>
                        <span style={{ fontSize: 13 }}>🔒</span> Not assigned
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input className="inp" style={{ width: 70, height: 36, textAlign: "center", fontWeight: 700 }}
                          inputMode="numeric" value={pct[k] ?? ""} onFocus={(e) => e.target.select()}
                          onChange={(e) => onPctChange(k, e.target.value.replace(/[^\d]/g, ""))} />
                        <span style={{ fontWeight: 700, color: "#64748b" }}>%</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={runView} disabled={busy}>{busy ? "Working…" : "View sample"}</button>
              <button className="btn btn-primary" onClick={sendToAudit} disabled={busy || !preview}>Send to audit</button>
            </div>
          </>
        )}
        {err && <div style={{ color: "#b91c1c", marginTop: 10, fontSize: 13 }}>{err}</div>}
      </div>

      {preview && (
        <div style={box}>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Sample preview — {selectedUser?.fullName}</div>
          {Object.entries(preview).map(([proc, data]) => (
            <div key={proc} style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ background: "#232c44", color: "#fff", borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{PLABEL[proc]}</span>
                <span className="cell-sub">{data?.sampled ?? (data?.records || []).length} of {data?.total ?? "—"} · {pct[proc]}%</span>
              </div>
              {(data?.records || []).length === 0
                ? <div className="muted" style={{ fontSize: 12 }}>No completed records to sample.</div>
                : <div className="table-wrap"><table className="tbl" style={{ fontSize: 12.5 }}>
                    <thead><tr><th>PID</th><th>Order</th><th>Status</th><th>Worked on</th></tr></thead>
                    <tbody>
                      {(data.records || []).map((r, i) => (
                        <tr key={i}><td className="mono">{r.mrn || r.uniqueId}</td><td>{r.orders || r.orderName || "—"}</td><td>{r.paStatus || "—"}</td><td className="cell-sub">{r.workedOn || r.workedAt || "—"}</td></tr>
                      ))}
                    </tbody>
                  </table></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}